import TopBar from "../../components/TopBar";

import "./index.scss";

const PrivacyPolicy = () => (
  <div className="privacy-policy">
    <TopBar heading="Privacy Policy" />
    <div className="bold-description">OMNI-ACTIVE® APP PRIVACY POLICY</div>
    <div className="description">
      <strong>What this document does.</strong> You and data about you are protected by various laws and guidance and
      Omni-Active® is also committed to upholding these and respecting your privacy. This document applies if we process
      your personal data (i.e. data about you) because you are going to (or currently) use our fitness App. It lets you
      know about how we collect, use, and protect any personal data we collect about you once you have downloaded or
      streamed a copy of the App on your mobile or handheld device. It also sets forth how we comply with the data
      protection laws and confirms what your rights are. Before you install our App, please read through this document
      and indicate your consent to our processing of your personal data (including your name, contact details, financial
      and device information) as described in this notice.
    </div>
    <div className="description">
      <strong>Breaking it down.</strong> To make life easier, we have broken down this information into chunks, so you
      can click through to see specific areas. This Notice provides details about:
    </div>
    <ul class="bullets">
      <li>Who we are and contacting us</li>
      <li>What personal data we collect</li>
      <li>Where do we get your personal data</li>
      <li>How we use your personal data</li>
      <li>If you don’t want Omni-Active® to have access to data you consented to</li>
      <li>Who we share your personal data with</li>
      <li>How we aim to protect your privacy</li>
      <li>How long we will keep your personal data</li>
      <li>Your rights!</li>
    </ul>
    <div className="bold-description">WHO WE ARE AND CONTACTING US</div>
    <div className="description">
      <strong>Who we are.</strong> In case you missed it somewhere along the way, the App is offered by Omni-Active®
      Fitness. We are a limited liability company (LLC) registered in Cleveland, OH in the United States.{" "}
    </div>
    <div className="description">
      <strong>If you have questions/complaints/concerns - give us a shout!</strong> If you have any queries regarding
      your personal data and how it may be used by Omni-Active®, then you can contact us at support@omniactive.com and
      by post at My Data Questions, Omni-Active® Fitness, 5175 Thornbury Blvd, Cleveland, OH 44124.
    </div>
    <div className="description">
      <strong>Let us know if things change.</strong> It is important that the personal data we hold about you is
      accurate and current. Please keep us informed if your personal data changes during our relationship with you as
      soon as possible.
    </div>
    <div className="description">
      <strong>Changes to the privacy policy.</strong>We keep our privacy policy under regular review. This version was
      last updated on March 1st, 2022. It may change and if it does, these changes will be posted on this page and,
      where appropriate, notified to you when you next start the App. The new policy may be displayed on-screen and you
      may be required to read and accept the changes to continue your use of the App. Where this will change how we use
      your data and we have relied on your consent, we will ask you to confirm you are happy for us to process your
      personal data in this way.
    </div>
    <div className="bold-description">WHAT PERSONAL DATA DO WE COLLECT?</div>
    <div className="description">We may collect, store and transfer the following information about you:</div>
    <ul class="bullets">
      <li>
        <strong>Identity Data: </strong>includes your first name, your last name, App ID, personal or online identifier
        or alias (or other identifier), title, date of birth, age, Internet Protocol address and gender.
      </li>
      <li>
        <strong> Contact Data:</strong> addresses (including billing and delivery addresses), and email address.
      </li>
      <li>
        <strong>Location Data:</strong> includes your current location disclosed by the Apple software (where you are
        using an Apple device) or the Google software (where you are using an Android device). We do not use separate
        location tracking software on our App.
      </li>
      <li>
        <strong>Marketing and Communications Data:</strong> includes your preferences in receiving marketing from us and
        our third parties and your communication preferences.
      </li>
      <li>
        <strong> Transaction Data:</strong> includes details about payments to and from you.
      </li>
      <li>
        <strong>Device Data:</strong> includes information about the device you use to and the unique device identifier
        for example your device’s IMEA number, the MAC address of the device’s wireless network interface, or the mobile
        phone used by the device, mobile network information, your mobile operating system, the type of mobile browser
        you use, time zone setting and also includes the IP address, device type, usernames and account details
      </li>
      <li>
        <strong>Profile Data:</strong> includes your user name, in-App purchase history, your interests, preferences,
        feedback and competition survey responses and any inferences drawn from any of personal data to create a profile
        about a consumer reflecting the consumer’s preferences, characteristics, psychological trends, predispositions,
        behavior, attitudes, intelligence, abilities, and aptitudes. Correspondence and communications. with us
        including relating to complaints, allegations, disputes and claims also fall into this category
      </li>
      <li>
        <strong>Content Data:</strong> includes information stored on your device, including login information, videos,
        photographs and audio recordings or other digital content, check-ins or your workout data that you input and
        upload and your social media handles, posts and information about your followers that you tag us in.
      </li>
      <li>
        <strong>Special Categories of Personal Data: </strong>There are limited situations in which we collect, store
        and use these “special categories” of more sensitive personal data. Where we do collect any special category
        personal data, we will do so based on your explicit consent. However, if you don’t consent this may limit
        certain features that we can provide in respect of the App.
      </li>
      <li>
        <strong>Aggregated Data:</strong> We also collect, use and share Aggregated Data such as statistical or
        demographic data for any purpose. Aggregated Data could come from your personal data but is not considered
        personal data in law as this data will not directly or indirectly reveal your identity. For example, we may
        aggregate your Usage Data to calculate the percentage of users accessing a specific App feature. However, if we
        combine or connect Aggregated Data with your personal data so that it can directly or indirectly identify you,
        we treat the combined data as personal data which will be used in accordance with this privacy policy. We may
        use anonymized and aggregated information for purposes that include testing our IT systems, research, data
        analysis, improving our App and our website and developing new products and services and for other purposes.
      </li>
    </ul>
    <div className="bold-description">Children</div>
    <div className="description">
      Our App and other services we provide are not intended for use by anyone under the age of 12 years and we do not
      knowingly collect personal data relating to anyone under the age of 12.
    </div>
    <div className="bold-description">WHERE DO WE GET YOUR PERSONAL DATA?</div>
    <div className="table-content">
      <table>
        <tr>
          <th>Where do we get it from</th>
          <th>How do we receive it?</th>
          <th>What types of personal data?</th>
        </tr>
        <tr>
          <td>You!</td>
          <td>
            <div className="description">We may collect, store and transfer the following information about you:</div>
            <ul class="bullets">
              <li>filling in forms on the App, or by corresponding with us (for example by email or chat).</li>
              <li>
                registering to use and/or downloading our App, subscribing to any of our services, searching for our App
                or services,
              </li>
              <li>making an in-App purchase,</li>
              <li>
                when you enter data about workouts you have done, and when you report a problem with an app, our
                services or any of our sites.
              </li>
              <li>if you contact us, we will keep a record of that correspondence</li>
              <li>
                communicate with us regarding one of our App or services, to ask a question, report a problem or for any
                other reason
              </li>
            </ul>
          </td>

          <td>
            <div className="description">Identity Data</div>
            <div className="description">Contact Data</div>
            <div className="description">Profile Data</div>
            <div className="description">Transaction Data</div>
            <div className="description">Marking and Communications Data</div>
            <div className="description">Location Data</div>
          </td>
        </tr>
        <tr>
          <td>Your device </td>
          <td>
            <div className="description">
              Each time you use our App we will automatically collect personal data. We collect this data using
              technologies similar to cookies.
            </div>
          </td>
          <td>
            <div className="description">Device Data</div>
            <div className="description">Content Data</div>
            <div className="description">Usage Data</div>
          </td>
        </tr>
        <tr>
          <td rowspan="3">Third Parties and other publicly available sources</td>
          <td>
            <div className="description">
              Credit reference agencies including those which carry out data cleansing services. Organizations that
              carry out research and analysis as follows:
            </div>
            <ul class="bullets">
              <li>Stripe</li>
            </ul>
          </td>
          <td>
            <div className="description">Identity Data</div>
            <div className="description">Location Data</div>
            <div className="description">Device Data</div>
            <div className="description">Profile Data</div>
            <div className="description">Usage Data</div>
            <div className="description">Contact Data</div>
            <div className="description">Marketing and Communications Data</div>
            <div className="description">Transaction Data</div>
          </td>
        </tr>
        <tr>
          <td>
            <div className="description">Publicly available information, including:</div>
            <ul class="bullets">
              <li>Social Medial Platforms including LinkedIn; Instagram, YouTube, Twitter or public Facebook page;</li>
              <li>Your professional advisors including lawyers, accountants and other advisors; and</li>
              <li>Government, local authorities or relevant regulators.</li>
            </ul>
          </td>
          <td>
            <div className="description">Identity Data</div>
            <div className="description">Profile Data</div>
            <div className="description">Contact Data</div>
          </td>
        </tr>
        <tr>
          <td>
            <div className="description">
              Organizations that help our App run and also help fix any problems, including:
            </div>
            <ul class="bullets">
              <li>Stripe</li>
              <li>Apple (where you are using an Apple device)</li>
              <li>Google (where you are using an Android device)</li>
            </ul>
          </td>
          <td>
            <div className="description">Identity Data</div>
            <div className="description">Profile Data</div>
            <div className="description">Contact Data</div>
          </td>
        </tr>
      </table>
    </div>
    <div className="bold-description">HOW WE USE YOUR DATA</div>
    <div className="description">
      See our table below to show you the purposes that we use each category of your personal data for and the lawful
      bases that we have for processing each of those types of personal data.
    </div>
    <div className="table-content">
      <table>
        <tr>
          <th>Purpose/activity</th>
          <th>Lawful Basis for processing</th>
        </tr>
        <tr>
          <td>
            <strong>To carry out identity checks so we know who we are entering into a contract with</strong>
          </td>
          <td>Processing is necessary for the performance of our contract with you.</td>
        </tr>
        <tr>
          <td>
            <strong>To deal with queries, complaints, claims, legal disputes submitted by you</strong>
          </td>
          <td>We have a legitimate interest to investigate any query claim, complaint or dispute submitted by you</td>
        </tr>
        <tr>
          <td>
            <strong>
              Analysis of your data that we collect and hold, process payments, send communications to our customers,
              provide us with legal, property or financial advice and generally help us deliver our products and
              services to you
            </strong>
          </td>
          <td>Processing is necessary for the performance of the contract with you</td>
        </tr>
        <tr>
          <td>
            <strong>
              Data analytics, statistical analysis and other research, including in respect of various data collecting
              technologies to help us improve our products and services to maintain and improve our services and/or
              products
            </strong>
          </td>
          <td>
            We have a legitimate interest in improving the online services and/or products we provide and user
            experience.
          </td>
        </tr>
        <tr>
          <td>
            <strong>To ensure the security of our IT systems and your login details</strong>
          </td>
          <td>
            We have a legitimate interest in ensuring the security of our IT systems and to protect your login details.
          </td>
        </tr>
        <tr>
          <td>
            <strong>Direct marketing and App marketing activities</strong>
          </td>
          <td>Your explicit consent</td>
        </tr>
        <tr>
          <td>
            <strong>
              Assisting government, local authorities, relevant regulators and the police in the prevention, detection,
              investigation of crime, prosecution of offenders and providing relevant information required.
            </strong>
          </td>
          <td>To comply with any legal obligations and regulatory obligations</td>
        </tr>
        <tr>
          <td>
            <strong>
              Keep and maintain appropriate records to ensure we can provide the app and any related services to you, so
              that we comply with our legal and regulatory obligations, have appropriate records in order to provide the
              app and any additional services to you and also so we have back-up data.
            </strong>
          </td>
          <td>To comply with any legal obligations and regulatory obligations.</td>
        </tr>
        <tr>
          <td>
            <strong>
              Content of workout analysis, selecting which workouts to present to you, collecting data of what workouts
              user undertake, how much they do and whether they undertake similar/ different workouts regularly
            </strong>
          </td>
          <td>
            We have a legitimate interest in improving the online services and/or products we provide and user
            experience
          </td>
        </tr>
        <tr>
          <td>
            <strong>
              Building a profile of your interests based on your activities within the App and other information
              available to use from your purchases and interactions with our website, marketing communications and
              online adverts, and from our partners such as social media and app store providers.
            </strong>
          </td>
          <td>
            We have a legitimate interest in improving the online services and/or products we provide and user
            experience. Where you decide to provide biometric information (e.g. your height and weight), we will rely on
            explicit consent.
          </td>
        </tr>
        <tr>
          <td>
            <strong>
              The app will temporarily store some information about your interaction with the app on your device. The
              information will be transferred to our secure systems using your telecommunications data or wireless
              network as soon as possible and once this data is transferred, this data will be deleted from your device
            </strong>
          </td>
          <td>
            We will rely on your consent to be able to access your device and store (and retrieve) information on it.
          </td>
        </tr>
      </table>
    </div>
    <div className="description">
      We will not use your personal data to target, segment, or profile individuals based on health (including
      pregnancy), negative financial status or condition, political affiliation or beliefs, racial or ethnic origin,
      religious or philosophical affiliation or beliefs, sex life or sexual orientation, trade union membership, or data
      relating to alleged or actual commission of a crime, for any unlawful or discriminatory purpose or in a manner
      that would be inconsistent with your reasonable expectation of privacy.
    </div>
    <div className="bold-description">IF YOU DON’T WANT OMNI-ACTIVE® TO HAVE ACCESS TO DATA YOU CONSENTED TO</div>

    <div className="bold-description">What to do if you change your mind</div>
    <div className="description">
      For some of your personal data you may have a legal, contractual or other requirement or obligation for you to
      provide us with your personal data. If you do not provide us with the requested personal data, we may not be able
      to properly perform our contract with you or comply with legal obligations and we may have to terminate our
      relationship. For other personal data you may not be under an obligation to provide it to us, but if you do not
      provide it then we may not be able to properly provide you all the functionality of the App or provide any
      additional services to you.
    </div>
    <div className="description">
      If you consented to provide us with certain personal data for processing, you may change your mind and withdraw
      consent at any time by contacting our customer support team at support@omniactive.com but that will not affect the
      lawfulness of any processing carried out before you withdraw your consent. We may still be entitled to hold and
      process the relevant personal data to the extent that we are entitled to do so on a basis other than your consent.
      Withdrawing consent may also have the same effects as not providing the information in the first place, for
      example, we may no longer be able to provide full functionality or certain services to you.
    </div>
    <div className="bold-description">Location Data</div>
    <div className="description">
      You can choose whether you want us to know your country and city in your Apple settings (where you are using an
      Apple device) or your Google settings (where you are using an Android device). You can withdraw your consent at
      any time by disabling Location Data in your settings
    </div>
    <div className="bold-description">Changing Marketing Preferences</div>
    <div className="description">
      You have the right to opt out of receiving marketing communications from us at any time, by:
    </div>
    <ol class="bullets">
      <li>updating your preferences in the App settings;</li>
      <li>
        informing us that you wish to change your marketing preferences by contacting our customer support team at
        support@omniactive.com
      </li>
      <li>making use of the simple “unsubscribe” link in emails; and/or</li>
    </ol>
    <div className="description">This will not stop service messages such as non-marketing communications.</div>
    <div className="bold-description">WHO WE SHARE YOUR PERSONAL DATA WITH</div>
    <div className="table-content">
      <table>
        <tr>
          <th>Third Party</th>
          <th>What types of personal data</th>
        </tr>
        <tr>
          <td>
            <div className="description">Omni-Active® Companies</div>
          </td>
          <td>
            <div className="description">Usage Data</div>
          </td>
        </tr>
        <tr>
          <td>
            <div className="description">
              Purchasers, investors, funders and advisers if we sell or negotiate to sell all or part of our business or
              assets or restructure our business whether by merger, re-organisation or otherwise
            </div>
          </td>
          <td>
            <div className="description">The following data will be Aggregated Data:</div>
            <div className="description">Usage Data</div>
            <div className="description">Location Data</div>
            <div className="description">Identity Data</div>
          </td>
        </tr>
        <tr>
          <td>
            <div className="description">
              Our professional advisors including lawyers, accountants and other advisors
            </div>
          </td>
          <td>
            <div className="description">Identity Data</div>
            <div className="description">Contact Data</div>
            <div className="description">Location Data</div>
            <div className="description">Marketing and Communications Data</div>
            <div className="description">Transaction Data</div>
            <div className="description">Usage Data</div>
            <div className="description">Device Data</div>
            <div className="description">Profile Data</div>
            <div className="description">Content Data</div>
          </td>
        </tr>
        <tr>
          <td>
            <div className="description">
              Other service providers and advisors to us including companies that support our IT, help us analyse the
              data we hold, process payments, send communications to our customers, provide us with legal, property or
              financial advice and generally help us deliver our products and services to you, including:
            </div>
            <ul class="bullets">
              <li>Apple (where you are using an Apple device)</li>
              <li>Google (where you are using an Android device)</li>
              <li>Stripe</li>
            </ul>
          </td>
          <td>
            <div className="description">Identity Data</div>
            <div className="description">Contact Data</div>
            <div className="description">Marketing and Communications Data</div>
            <div className="description">Location Data</div>
            <div className="description">Usage Data</div>
            <div className="description">Device Data</div>
            <div className="description">Profile Data</div>
            <div className="description">Content Data</div>
            <div className="description">Transaction Data</div>
          </td>
        </tr>
        <tr>
          <td>
            <div className="description">
              Organizations who carry out research, analysis and/or data cleansing services, including:
            </div>
            <ul class="bullets">
              <li>Stripe</li>
            </ul>
          </td>
          <td>
            <div className="description">Usage Data</div>
            <div className="description">Identity Data</div>
            <div className="description">Contact Data</div>
            <div className="description">Profile Data</div>
            <div className="description">Identity Data</div>
            <div className="description">Marketing and Communications Data</div>
            <div className="description">Special Categories of Personal Data</div>
            <div className="description">Location Data</div>
            <div className="description">Transaction Data</div>
          </td>
        </tr>
        <tr>
          <td>
            <div className="description">
              Governmental bodies, regulators, law enforcement agencies, security services, courts/tribunals and
              insurers including where we are required to do so in order to comply with our legal obligations and the
              administration of justice.
            </div>
          </td>
          <td>
            <div className="description">Usage Data</div>
            <div className="description">Identity Data</div>
            <div className="description">Contact Data</div>
            <div className="description">Location Data</div>
            <div className="description">Profile Data</div>
            <div className="description">Content Data</div>
            <div className="description">Device Data</div>
            <div className="description">Transaction Data</div>
          </td>
        </tr>
      </table>
    </div>
    <div className="bold-description">HOW WE PROTECT YOUR DATA</div>
    <div className="bold-description">Our controls</div>
    <div className="description">
      Omni-Active® is committed to keeping your personal data safe and secure and so we have numerous security measures
      in place to protect the loss, misuse and alteration of information under our control. Our security measures
      include: -
    </div>
    <ul class="bullets">
      <li>encryption of personal data;</li>
      <li>regular cyber security assessments of all service providers who may handle your personal data;</li>
      <li>regular planning to ensure we are ready to respond to cyber security attacks and data security incidents;</li>
      <li>security controls which protect our IT systems infrastructure and our premises from unauthorized access;</li>
      <li>internal policies setting out our data security rules for our personnel</li>
    </ul>
    <div className="description">
      We take data security very seriously and will use all reasonable measures to protect the integrity and security of
      the personal data we collect about you.
    </div>
    <div className="bold-description">WHAT YOU CAN DO TO HELP PROTECT YOUR DATA</div>
    <div className="description">
      You should always be cautious when sharing your personal data. No one from our company will ever ask you to
      confirm any bank account or credit card details via email. If you receive an email claiming to be from
      Omni-Active® asking you to do so, please ignore it and do not respond.
    </div>
    <div className="description">
      If you are using a computing device in a public location, we recommend that you always log out and close the
      website browser when you complete an online session.
    </div>
    <div className="description">
      In addition, we recommend that you take the following security measures to enhance your online safety:
    </div>
    <ul class="bullets">
      <li>
        When creating a password, use a difficult word/number combination of at least 8 characters and something that is
        not easily guessed or something that cannot be easily obtained such as your name, email address, or other
        personal data that can be easily obtained.
      </li>
      <li>Frequently change your password (you can do this in your account settings.</li>
      <li>Avoid using the same password for different online accounts).</li>
    </ul>
    <div className="bold-description">HOW LONG WE KEEP YOUR DATA</div>
    <div className="description">
      We will not retain your personal data for longer than necessary for the purpose for which is has been obtained and
      then for as long as there is any risk of a potential claim, which will be dependent upon the limitation period for
      the particular type of claim. Various laws, accounting and regulatory requirements applicable to us also require
      us to retain certain records for specific amounts of time. In relation to your personal data, we will hold this
      only for so long as we require that personal data for legal or regulatory reasons or for legitimate organizational
      purposes. We will not keep your data for longer than is reasonably necessary for the purposes for which we collect
      them.
    </div>
    <div className="bold-description">YOUR RIGHTS</div>
    <div className="description">You have the following rights in relation to your personal data: </div>
    <ul class="bullets">
      <li>
        The <strong> right to be informed </strong>about how your personal data is being used.
      </li>
      <li>
        The <strong> right to request access </strong>to personal data we hold about you.
      </li>
      <li>
        The <strong>right to ask us to update and correct any out-of-date or incorrect</strong> personal data that we
        hold about you.
      </li>
      <li>
        The <strong> right to object to processing of your personal data and/or to withdraw any consent</strong> you
        have given us and to opt out of any marketing communications that we may send you.
      </li>
      <li>
        The <strong> right to restrict processing</strong> of your personal data.
      </li>
      <li>
        The <strong>right to object to certain automated decision</strong>-making processes using your personal data
        including profiling.
      </li>

      <li>
        The <strong> right to request that we erase</strong> your personal data in certain circumstances{" "}
        <strong>(the right to be forgotten)</strong> for example when the data are no longer necessary for the purpose
        for which we collected them.
      </li>
      <li>
        <strong> California Residents: </strong>If you reside in California and have provided personal data to us, you
        may request information about our disclosure of certain categories of personal information to third parties for
        their direct marketing purposes. Such requests must be submitted to us at the following address: email to
        support@omniactive.com or post to My Data Questions, Omni-Active® Fitness, 5175 Thornbury Blvd, Cleveland, OH
        44124.
      </li>
      <li>
        <strong> The right to object to direct marketing and profiling.</strong> You may have the right to opt out of
        some automated processing, including profiling, at any time by: 
        <div className="description">
          1. informing us that you wish to opt out of automated processing by contacting our customer support team at
          support@omniactive.com; and/or 
        </div>
        <div className="description">
          2. contacting us by email to support@omniactive.com or post to My Data Questions, Omni-Active® Fitness, 5175
          Thornbury Blvd, Cleveland, OH 44124. The{" "}
          <strong>
            right to have your personal data provided to you by us in a structured, commonly used and machine-readable
            format
          </strong>{" "}
          and transmitted to another data controller. This is known as the <strong> right to data portability.</strong>
        </div>
      </li>
    </ul>
    <div className="description">
      You should note that some of these rights, for example the right to require us to transfer your data to another
      service provider or the right to object to automated decision making, may not apply as they have specific
      requirements and exemptions which apply to them and they may not apply to personal data recorded and stored by us.
      However, some have no conditions attached, so your right to withdraw consent or object to processing for direct
      marketing are absolute rights.
    </div>
    <div className="description">
      We may need to request specific information from you to help us confirm your identity and ensure your right to
      access the information (or to exercise any of your other rights). This is another appropriate security measure to
      ensure that personal data is not disclosed to any person who has no right to receive it.
    </div>
    <div className="description">
      The law in this area is complex and this document only gives you a general summary of your legal rights in respect
      of personal data.
    </div>
    <div className="description">
      If you wish to exercise any of the above rights, you can always contact us using the details set out in the{" "}
      <strong>‘Who we are and contacting us’</strong> section above.
    </div>
    <div className="bold-description">We aren’t responsible for everyone!</div>
    <div className="description">
      Our sites may, from time to time, contain links to and from the websites of our partner networks, advertisers and
      affiliates. Please note that these websites and any services that may be accessible through them have their own
      privacy policies and that we do not accept any responsibility or liability for these policies or for any personal
      data that may be collected through these websites or services, such as contact and location data. Please check
      these policies before you submit any personal data to these websites or use these services.
    </div>
  </div>
);

export default PrivacyPolicy;
